import React, { Component, Fragment } from "react";
import { Container } from "semantic-ui-react";
import Helmet from "react-helmet";
import { StaticQuery, graphql } from "gatsby";

// Localization
import { IntlProvider } from "react-intl";
import { Location } from "@reach/router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import localizedFormat from "dayjs/plugin/localizedFormat";

import { Footer } from "../footer";
import { TopMenu, TopMenuMobile, ModalMenu } from "../header";
import "./graphql-fragments";
import Redirect from "../i18link/redirect";

// Locale data
import en from "../../locale/en";
import fr from "../../locale/fr";
import de from "../../locale/de";
import ja from "../../locale/ja";

// Polyfill
import "intl-pluralrules";

// Black Friday discount
import { BlackFridayContext, isBlackFridayFn } from "../black-friday/black-friday-context";

require("dayjs/locale/de");
require("dayjs/locale/fr");
require("dayjs/locale/ja");

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

// import { Redirect } from "../i18link";

const messages = { en, fr, de, ja };
// addLocaleData([...enData, ...frData, ...deData, ...jaData]);

export const SiteMapContext = React.createContext();

const createAbsUri = (uri) => {
  let ret = uri || "";

  // Add the scheme and hostname for relative uri only
  if (!ret || ret[0] === "/") {
    if (process.env.NODE_ENV !== "development") {
      ret = `https://www.screenpresso.com` + ret;
    } else if (typeof window !== "undefined") {
      ret = window.location.origin + ret;
    }
  }

  return ret;
};

class LayoutInternal extends Component {
  state = { visible: false };
  toggleVisibility = () => {
    const body = document.body;
    body.classList.toggle("noscroll", !this.state.visible);
    this.setState({ visible: !this.state.visible });
  };

  render() {
    const {
      children,
      pageId,
      locale,
      title,
      staticQuery,
      featuredImage,
      location,
      canonicalpath,
      description,
    } = this.props;
    const { visible } = this.state;
    const siteTitle =
      staticQuery && staticQuery.site && staticQuery.site.siteMetadata
        ? staticQuery.site.siteMetadata.title
        : "Screenpresso";

    const meta =
      staticQuery && staticQuery.site && staticQuery.site.siteMetadata
        ? staticQuery.site.siteMetadata
        : { version: "x.x" };

    const siteMap =
      staticQuery && staticQuery.siteMap && staticQuery.siteMap.edges
        ? staticQuery.siteMap.edges
        : [];

    const siteDownloadLinks =
      staticQuery && staticQuery.site && staticQuery.site.siteMetadata
        ? staticQuery.site.siteMetadata.downloadLinks
        : undefined;

    const siteMapPerId = {};
    siteMap.forEach((s) => {
      if (s.node && s.node.context && s.node.context.id) {
        siteMapPerId[s.node.context.id] = siteMapPerId[s.node.context.id] || {};

        siteMapPerId[s.node.context.id][s.node.context.language] = {
          text: s.node.context.name,
          path: s.node.path,
          matchPath: s.node.matchPath,
          id: s.node.context.id,
        };
      }
    });

    const isSummary = location ? /summary/.test(location.pathname) : false;
    const isCloud = location ? /cloud/.test(location.pathname) : false;

    const locales = Object.keys(siteMapPerId[pageId]).map((language) => ({
      locale: language,
      ...siteMapPerId[pageId][language],
    }));

    // Adjust for client dynamic part
    const currentPage = siteMapPerId[pageId][locale];
    const basepath = currentPage.path;
    if (currentPage.matchPath && /\/\*$/.test(currentPage.matchPath)) {
      const suffix = location.pathname.replace(currentPage.path, "");
      locales.forEach((item) => {
        item.path += suffix;
      });
    }

    const titleToDisplay = title || siteTitle;

    // Alternate pages
    // https://support.google.com/webmasters/answer/189077?hl=en
    // for instance: <link rel="alternate" hreflang="lang_code" href="url_of_page" />
    const alternates = locales.map((item, index) => ({
      rel: "alternate",
      hrefLang: item.locale,
      href: createAbsUri(item.path),
    }));

    alternates.push({
      rel: "alternate",
      hrefLang: "x-default",
      href: createAbsUri(siteMapPerId[pageId]["en"].path),
    });

    alternates.push({
      rel: "canonical",
      href: isSummary
        ? createAbsUri(canonicalpath)
        : createAbsUri(siteMapPerId[pageId][locale].path),
    });

    const pageDescription =
      messages[locale][`page.${pageId}.description`] || description || "";

    const isBlackFriday = isBlackFridayFn()

    // set date format
    dayjs.locale(locale);

    return (
      <IntlProvider locale={locale} key={locale} messages={messages[locale]}>
        <Fragment>
          <Helmet
            htmlAttributes={{
              lang: locale,
            }}
            title={titleToDisplay}
            link={[
              {
                rel: "apple-touch-icon",
                sizes: "180x180",
                content: "/apple-touch-icon.png",
              },
              {
                rel: "icon",
                type: "image/png",
                sizes: "32x32",
                content: "/favicon-32x32.png",
              },
              {
                rel: "icon",
                type: "image/png",
                sizes: "16x16",
                content: "/favicon-16x16.png",
              },
              { rel: "manifest", href: "/site.webmanifest" },
              {
                rel: "mask-icon",
                href: "/safari-pinned-tab.svg",
                color: "#5bbad5",
              },
              ...alternates,
            ]}
          >
            {/* General tags */}
            <meta name="description" content={pageDescription} />
            <meta
              name="keywords"
              content="Screenpresso, Screen capture, Screen grabber, image editor, screencast, video capture"
            />
            {/* OpenGraph tags */}
            <meta property="og:type" content="website" />
            <meta property="og:title" content={titleToDisplay} />
            <meta
              property="og:url"
              content={createAbsUri(siteMapPerId[pageId][locale].path)}
            />
            <meta property="og:description" content={pageDescription} />
            <meta
              property="og:image"
              content={createAbsUri(featuredImage || "/screenpresso-logo.png")}
            />
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:site" content="@screenpresso" />
            <meta property="twitter:creator" content="@jcbaey" />
            {isSummary && <meta property="robots" content="noindex" />}
          </Helmet>
          <SiteMapContext.Provider
            value={{
              sitemap: siteMapPerId,
              locale,
              dl: siteDownloadLinks,
              basepath,
            }}
          >
            <BlackFridayContext.Provider value={isBlackFriday}>
              {!isSummary && (
                <>
                  <TopMenu
                    siteTitle={siteTitle}
                    locale={locale}
                    siteMap={siteMapPerId}
                    locales={locales}
                  />
                  <TopMenuMobile
                    siteTitle={siteTitle}
                    locale={locale}
                    onClick={this.toggleVisibility}
                  />
                  <ModalMenu
                    visible={visible}
                    onClose={this.toggleVisibility}
                    siteTitle={siteTitle}
                    locale={locale}
                    locales={locales}
                    siteMap={siteMapPerId}
                  />
                </>
              )}

              <div
                className={
                  isSummary ? "summary" : isCloud ? "main-cloud" : "main"
                }
              >
                <Container fluid={isCloud}>{children}</Container>
              </div>

              {!isSummary && (
                <>
                  <Footer
                    siteMeta={meta}
                    locales={locales}
                    locale={locale}
                    siteMap={siteMapPerId}
                    releases={staticQuery.releases.edges}
                  />
                  <Redirect locale={locale} locales={locales} />
                </>
              )}
            </BlackFridayContext.Provider>
          </SiteMapContext.Provider>
        </Fragment>
      </IntlProvider>
    );
  }
}

const Layout = (props) => (
  <StaticQuery
    query={graphql`query LayoutQuery {
  site {
    ...SiteVersionQuery
    ...SiteTitleQuery
    ...SiteUmDownloadLinksQuery
  }
  siteMap: allSitePage {
    ...LanguageMap
  }
  releases: allMarkdownRemark(
    filter: {frontmatter: {tags: {eq: "release"}}}
    sort: {frontmatter: {date: DESC}}
    limit: 4
  ) {
    edges {
      node {
        fields {
          id
        }
        frontmatter {
          title
        }
      }
    }
  }
}`}
    render={(data) => {
      return (
        <Location>
          {({ location }) => (
            <LayoutInternal staticQuery={data} location={location} {...props} />
          )}
        </Location>
      );
    }}
  />
);

export default Layout;